import { RouteRecordRaw } from 'vue-router';

/**
 * 路由meta对象参数说明
 * meta: {
 *      title:          菜单栏及 tagsView 栏、菜单搜索名称（国际化）
 *      isLink：        是否超链接菜单，开启外链条件，`1、isLink: 链接地址不为空`
 *      isHide：        是否隐藏此路由
 *      isKeepAlive：   是否缓存组件状态
 *      isAffix：       是否固定在 tagsView 栏上
 *      isIframe：      是否内嵌窗口，开启条件，`1、isIframe:true 2、isLink：链接地址不为空`
 *      roles：         当前路由权限标识，取角色管理。控制路由显示、隐藏。超级管理员：admin 普通角色：common
 *      icon：          菜单、tagsView 图标，阿里：加 `iconfont xxx`，fontawesome：加 `fa xxx`
 * }
 */

/**
 * 定义动态路由
 * 前端添加路由，请在顶级节点的 `children 数组` 里添加
 * @description 未开启 isRequestRoutes 为 true 时使用（前端控制路由），开启时第一个顶级 children 的路由将被替换成接口请求回来的路由数据
 * @description 各字段请查看 `/@/views/system/menu/component/addMenu.vue 下的 ruleForm`
 * @returns 返回路由菜单数据
 */
export const dynamicRoutes: Array<RouteRecordRaw> = [
    {
        path: '/',
        name: '/',
        component: () => import('/@/layout/index.vue'),
        meta: {
            isKeepAlive: true,
        },
        children: [
            {
                path: "/dashboard",
                name: "dashboard",
                component: () => import('/@/views/dashboard/index.vue'),
                meta: {
                    title: "仪表盘",
                    isLink: "",
                    isHide: false,
                    isKeepAlive: true,
                    isAffix: false,
                    isIframe: false,
                    permissions: ["Dashboard.view"],
                    icon: ""
                },
                children:[
                    {
                        path: "/dashboard/nationalMonthlyActivationData/:id",
                        name: "nationalMonthlyActivationData",
                        props: true,
                        component: () => import('/@/views/dashboard/nationalMonthlyActivationData.vue'),
                        meta: {
                            title: "国家月度激活数量",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["Dashboard.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/dashboard/manufacturerMonthlyActivationData/:id",
                        name: "manufacturerMonthlyActivationData",
                        props: true,
                        component: () => import('/@/views/dashboard/manufacturerMonthlyActivationData.vue'),
                        meta: {
                            title: "厂商月度激活数量",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["Dashboard.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/dashboard/allMonthlyActivationData",
                        name: "allMonthlyActivationData",
                        props: true,
                        component: () => import('/@/views/dashboard/allMonthlyActivationData.vue'),
                        meta: {
                            title: "全部月度激活数量",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["Dashboard.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/dashboard/childPage/nationalMonthlyModelData",
                        name: "nationalMonthlyModelData",
                        props: true,
                        component: () => import('/@/views/dashboard/childPage/nationalMonthlyModelData.vue'),
                        meta: {
                            title: "国家月度型号数量",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["Dashboard.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/dashboard/childPage/manufacturerActivationDayData",
                        name: "manufacturerActivationDayData",
                        props: true,
                        component: () => import('/@/views/dashboard/childPage/manufacturerActivationDayData.vue'),
                        meta: {
                            title: "国家月度型号数量",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["Dashboard.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/dashboard/childPage/dashboardDeviceList",
                        name: "dashboardDeviceList",
                        props: true,
                        component: () => import('/@/views/dashboard/childPage/dashboardDeviceList.vue'),
                        meta: {
                            title: "设备列表",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["Dashboard.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/dashboard/childPage/dashboardDailyView",
                        name: "dashboardDailyView",
                        props: true,
                        component: () => import('/@/views/dashboard/childPage/dashboardDailyView.vue'),
                        meta: {
                            title: "日视图",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["Dashboard.view"],
                            icon: ""
                        }
                    },
                ]
            },
            {
                path: "/manufacturer",
                name: "manufacturer",
                meta: {
                    title: "OEM管理",
                    isLink: "",
                    isHide: false,
                    isKeepAlive: true,
                    isAffix: false,
                    isIframe: false,
                    permissions: ["OEM.view", "Customer.view", "ProduceModelList.view"],
                    icon: ""
                },
                children:[
                    {
                        path: "/iot/manufacturer/manufacturermanage/index",
                        name: "manufacturermanage",
                        component: () => import('/@/views/iot/manufacturer/manufacturermanage/index.vue'),
                        meta: {
                            title: "OEM厂商管理",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["OEM.view"],
                            icon: ""
                        },
                         children: [
                            {
                                path: "/oem/detail/:id",
                                name: "OemDetail",
                                props: true,
                                component: () => import('/@/views/oem/detail.vue'),
                                meta: {
                                    title: "厂商详情",
                                    isLink: "",
                                    isHide: true,
                                    isKeepAlive: true,
                                    isAffix: false,
                                    isIframe: false,
                                    permissions: ["OEM.Detail.view"],
                                    icon: ""
                                }
                            },
                            {
                                path: "/oem/create",
                                name: "CreateOem",
                                component: () => import('/@/views/oem/oemform.vue'),
                                meta: {
                                    title: "创建厂商",
                                    isLink: "",
                                    isHide: true,
                                    isKeepAlive: true,
                                    isAffix: false,
                                    isIframe: false,
                                    permissions: ["OEM.create"],
                                    icon: ""
                                }
                            },
                            {
                                path: "/oem/edit/:id",
                                name: "EditOem",
                                props: true,
                                component: () => import('/@/views/oem/oemform.vue'),
                                meta: {
                                    title: "编辑厂商",
                                    isLink: "",
                                    isHide: true,
                                    isKeepAlive: false,
                                    isAffix: false,
                                    isIframe: false,
                                    permissions: ["OEM.edit"],
                                    icon: ""
                                }
                            },
                            {
                                path: "/oemapp/:id",
                                name: "OEMAppSetting",
                                props: true,
                                component: () => import('../views/oem/oemAppForm.vue'),
                                meta: {
                                    title: "应用配置",
                                    isLink: "",
                                    isHide: true,
                                    isKeepAlive: false,
                                    isAffix: false,
                                    isIframe: false,
                                    permissions: ["OEM.edit"],
                                    icon: ""
                                }
                            }
                ]
                    },
                    {
                        path: "/iot/manufacturer/nationalagency/index",
                        name: "nationalagency",
                        component: () => import('/@/views/iot/manufacturer/nationalagency/index.vue'),
                        meta: {
                            title: "国家代理管理",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["Customer.view"],
                            icon: ""
                        },
                        children: [
                            {
                                path: "/distributor/detail/:id",
                                name: "DistributorDetail",
                                props: true,
                                component: () => import('/@/views/distributor/detail.vue'),
                                meta: {
                                    title: "国家代理详情",
                                    isLink: "",
                                    isHide: true,
                                    isKeepAlive: true,
                                    isAffix: false,
                                    isIframe: false,
                                    permissions: ["Customer.Detail.view"],
                                    icon: ""
                                }
                            },
                            {
                                path: "/distributor/create",
                                name: "CreateDistributor",
                                component: () => import('/@/views/distributor/distributorform.vue'),
                                meta: {
                                    title: "创建国家代理",
                                    isLink: "",
                                    isHide: true,
                                    isKeepAlive: true,
                                    isAffix: false,
                                    isIframe: false,
                                    permissions: ["Customer.create"],
                                    icon: ""
                                }
                            },
                            {
                                path: "/distributor/edit/:id",
                                name: "EditDistributor",
                                props: true,
                                component: () => import('/@/views/distributor/distributorform.vue'),
                                meta: {
                                    title: "编辑国家代理",
                                    isLink: "",
                                    isHide: true,
                                    isKeepAlive: false,
                                    isAffix: false,
                                    isIframe: false,
                                    permissions: ["Customer.edit"],
                                    icon: ""
                                }
                            }
                        ]

                    },
                    {
                        path: "/iot/manufacturer/regionalagent/index",
                        name: "regionalagent",
                        component: () => import('/@/views/iot/manufacturer/regionalagent/index.vue'),
                        meta: {
                            title: "区域代理管理",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["RegionalDealer.view"],
                            icon: ""
                        },
                        children:[
                            {
                                path: "manufacturer/regionalagent/regionalagentdetail/:id",
                                name: "regionalagentdetail",
                                props: true,
                                component: () => import('/@/views/iot/manufacturer/regionalagent/regionalagentdetail.vue'),
                                meta: {
                                    title: "区域代理详情",
                                    isLink: "",
                                    isHide: true,
                                    isKeepAlive: true,
                                    isAffix: false,
                                    isIframe: false,
                                    permissions: ["RegionalDealer.Detail.view"],
                                    icon: ""
                                }
                            },
                            {
                                path: "/manufacturer/regionalagent/create",
                                name: "regionalagentadd",
                                component: () => import('/@/views/iot/manufacturer/regionalagent/regionalagentaddedit.vue'),
                                meta: {
                                    title: "创建区域代理",
                                    isLink: "",
                                    isHide: true,
                                    isKeepAlive: true,
                                    isAffix: false,
                                    isIframe: false,
                                    permissions: ["RegionalDealer.create"],
                                    icon: ""
                                }
                            },
                            {
                                path: "/manufacturer/regionalagent/edit/:id",
                                name: "regionalagentedit",
                                props: true,
                                component: () => import('/@/views/iot/manufacturer/regionalagent/regionalagentaddedit.vue'),
                                meta: {
                                    title: "编辑区域代理",
                                    isLink: "",
                                    isHide: true,
                                    isKeepAlive: false,
                                    isAffix: false,
                                    isIframe: false,
                                    permissions: ["RegionalDealer.edit"],
                                    icon: ""
                                }
                            }
                        ]
                    },
                ],
            },
            {
                path: "/iot/produce",
                name: "producemnt",
                meta: {
                    title: "产品管理",
                    isLink: "",
                    isHide: false,
                    isKeepAlive: true,
                    isAffix: false,
                    isIframe: false,
                    permissions: ["ProduceList.view", "ProduceTypeList.view", "ProduceModelList.view"],
                    icon: ""
                },
                children: [
                    {
                        path: "/iot/produce/producetype/index",
                        name: "producetype",
                        component: () => import('/@/views/iot/produce/producetype/index.vue'),
                        meta: {
                            title: "产品类型列表",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["ProduceTypeList.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/produce/producelist/index",
                        name: "producelist",
                        component: () => import('/@/views/iot/produce/producelist/index.vue'),
                        meta: {
                            title: "产品列表",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["ProduceList.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/produce/producemodel/index",
                        name: "producemodel",
                        component: () => import('/@/views/iot/produce/producemodel/index.vue'),
                        meta: {
                            title: "产品型号列表",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["ProduceModelList.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/produce/createproducemodel",
                        name: "CreateProduceModel",
                        component: () => import('/@/views/iot/produce/producemodel/producemodelform.vue'),
                        meta: {
                            title: "创建产品型号",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["ProduceModelList.create"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/produce/editproducemodel/:produceModelId",
                        name: "EditProduceModel",
                        props: true,
                        component: () => import('/@/views/iot/produce/producemodel/producemodelform.vue'),
                        meta: {
                            title: "编辑产品型号",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["ProduceModelList.edit"],
                            icon: ""
                        }
                    }
                ]
            },
            {
                path: "/iot/devices",
                name: "devicemnt",
                meta: {
                    title: "设备管理",
                    isLink: "",
                    isHide: false,
                    isKeepAlive: true,
                    isAffix: false,
                    isIframe: false,
                    permissions: ["DeviceList.view", "DeviceBatchList.view", "OTAList.view"],
                    icon: ""
                },
                children: [
                    {
                        path: "/iot/devices/devicelist/index",
                        name: "devicelist",
                        component: () => import('/@/views/iot/devices/devicelist/index.vue'),
                        meta: {
                            title: "设备列表",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["DeviceList.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/devices/subdevicelist/index/:gatewayid",
                        name: "subdevicelist",
                        props:true,
                        component: () => import('/@/views/iot/devices/devicelist/index.vue'),
                        meta: {
                            title: "子设备列表",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["DeviceList.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/devices/devicebatchlist/index",
                        name: "devicebatchlist",
                        component: () => import('/@/views/iot/devices/devicebatchlist/index.vue'),
                        meta: {
                            title: "批次管理",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["DeviceBatchList.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/devices/detail/:id",
                        name: "DeviceDetail",
                        props: true,
                        component: () => import('/@/views/iot/devices/devicelist/DeviceDetail.vue'),
                        meta: {
                            title: "设备详情",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: false,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["DeviceList.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/devices/gatewaylog/:id",
                        name: "GatewayLog",
                        props: true,
                        component: () => import('/@/views/iot/devices/devicelist/GatewayLog.vue'),
                        meta: {
                            title: "网关日志",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: false,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["DeviceList.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/ota/otaupdate/index",
                        name: "otaupdate",
                        component: () => import('/@/views/iot/ota/otaupdate/index.vue'),
                        meta: {
                            title: "OTA升级",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["OTAList.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/ota/otaupdate/batchupdate",
                        name: "batchupdate",
                        component: () => import('/@/views/iot/ota/otaupdate/batchupdate.vue'),
                        meta: {
                            title: "验证",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["OTAList.verify"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/ota/otaupdate/updatedetail",
                        name: "updatedetail",
                        component: () => import('/@/views/iot/ota/otaupdate/updatedetail.vue'),
                        meta: {
                            title: "升级详情",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["OTAList.Detail.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/ota/otaupdate/updatelog",
                        name: "updatelog",
                        component: () => import('/@/views/iot/ota/otaupdate/updatelog.vue'),
                        meta: {
                            title: "升级日志",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["OTAList.Detail.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/devices/batchdevicelist/:batchId",
                        name: "BatchDeviceList",
                        props: true,
                        component: () => import('/@/views/iot/devices/devicebatchlist/batchDeviceList.vue'),
                        meta: {
                            title: "设备列表",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["DeviceBatchList.view"],
                            icon: ""
                        }
                    }
                ]
            },
            {
                path: "/iot/settings",
                name: "settingsmnt",
                meta: {
                    title: "系统管理",
                    isLink: "",
                    isHide: false,
                    isKeepAlive: true,
                    isAffix: false,
                    isIframe: false,
                    permissions: ["AppUserList.view", "RoleList.view", "AdminUserList.view", "WhiteList.add", "UserDeregister.view", "AppVersionList.view", "OfflineNotice.view"],
                    icon: ""
                },
                children: [
                    {
                        path: "/iot/settings/userlist/index",
                        name: "userlist",
                        component: () => import('/@/views/iot/settings/userlist/index.vue'),
                        meta: {
                            title: "App用户管理",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["AppUserList.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/rolelist/index",
                        name: "RoleList",
                        component: () => import('/@/views/role/index.vue'),
                        meta: {
                            title: "角色管理",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["RoleList.view"],
                            icon: "",
                        }
                    },
                    {
                        path: "/rolelist/create",
                        name: "RoleCreate",
                        component: () => import('/@/views/role/roleform.vue'),
                        meta: {
                            title: "创建角色",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["RoleList.create"],
                            icon: ""
                        }
                    },
                    {
                        path: "/rolelist/edit/:id",
                        name: "RoleEdit",
                        props: true,
                        component: () => import('/@/views/role/roleform.vue'),
                        meta: {
                            title: "编辑角色",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["RoleList.edit"],
                            icon: ""
                        }
                    },
                    {
                        path: "/rolelist/view/:id",
                        name: "RoleDetail",
                        props: true,
                        component: () => import('/@/views/role/roledetail.vue'),
                        meta: {
                            title: "查看角色",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["RoleList.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/adminUser/index",
                        name: "AdminUserList",
                        component: () => import('/@/views/adminUser/index.vue'),
                        meta: {
                            title: "系统用户管理",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["AdminUserList.view"],
                            icon: "",
                        }
                    },
                    {
                        path: "/adminUserList/create",
                        name: "AdminUserCreate",
                        component: () => import('../views/adminUser/userform.vue'),
                        meta: {
                            title: "创建系统用户",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["AdminUserList.create"],
                            icon: ""
                        }
                    },
                    {
                        path: "/adminUserList/edit/:id",
                        name: "AdminUserEdit",
                        props: true,
                        component: () => import('../views/adminUser/userform.vue'),
                        meta: {
                            title: "修改系统用户",
                            isLink: "",
                            isHide: true,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["AdminUserList.edit"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/settings/whiteList/index",
                        name: "WhiteList",
                        component: () => import('/@/views/iot/settings/whiteList/index.vue'),
                        meta: {
                            title: "白名单配置",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["WhiteList.create"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/settings/userdestruction/index",
                        name: "userdestruction",
                        component: () => import('/@/views/iot/settings/userdestruction/index.vue'),
                        meta: {
                            title: "用户注销记录",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["UserDeregister.view"],
                            icon: ""
                        }
                    },
                    // {
                    //     path: "/iot/settings/appversion/index",
                    //     name: "appversion",
                    //     component: () => import('/@/views/iot/settings/appversion/index.vue'),
                    //     meta: {
                    //         title: "App版本更新",
                    //         isLink: "",
                    //         isHide: false,
                    //         isKeepAlive: true,
                    //         isAffix: false,
                    //         isIframe: false,
                    //         permissions: ["AppVersionList.view"],
                    //         icon: ""
                    //     }
                    // },
                    {
                        path: "/iot/settings/pusherrorlog",
                        name: "pusherrorlog",
                        component: () => import('/@/views/iot/settings/pusherrorlog/index.vue'),
                        meta: {
                            title: "推送失败日志管理",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["AppVersionList.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/settings/offlineNotice",
                        name: "offlineNotice",
                        component: () => import('/@/views/iot/settings/offlineNotice/index.vue'),
                        meta: {
                            title: "离线告警管理",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["OfflineNotice.view"],
                            icon: ""
                        }
                    }
                ]
            },
            {
                path: "/iot/version",
                name: "version",
                meta: {
                    title: "版本管理",
                    isLink: "",
                    isHide: false,
                    isKeepAlive: true,
                    isAffix: false,
                    isIframe: false,
                    permissions: ["AppVersionList.view"],
                    icon: ""
                },
                children:[
                    {
                        path: "/iot/version/appversionupgrade/index",
                        name: "appversionupgrade",
                        component: () => import('/@/views/iot/version/appversionupgrade/index.vue'),
                        meta: {
                            title: "App版本更新",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["AppVersionList.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/version/appversionstatistics/index",
                        name: "appversionstatistics",
                        component: () => import('/@/views/iot/version/appversionstatistics/index.vue'),
                        meta: {
                            title: "App版本统计",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["AppVersionStat.view"],
                            icon: ""
                        }
                    },
                    {
                        path: "/iot/version/gatewayversioninfo/index",
                        name: "gatewayversioninfo",
                        component: () => import('/@/views/iot/version/gatewayversioninfo/index.vue'),
                        meta: {
                            title: "网关版本数据",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["GatewayVersionStat.view"],
                            icon: ""
                        },
                        children:[
                            {
                                path: "/version/gatewayversioninfo/gatewayversiondetail/:id",
                                name: "gatewayversiondetail",
                                props: true,
                                component: () => import('/@/views/iot/version/gatewayversioninfo/gatewayversiondetail.vue'),
                                meta: {
                                    title: "网关型号数据",
                                    isLink: "",
                                    isHide: true,
                                    isKeepAlive: true,
                                    isAffix: false,
                                    isIframe: false,
                                    permissions: ["GatewayVersionStat.view"],
                                    icon: ""
                                }
                            },
                        ]
                    },
                    {
                        path: "/iot/version/childdeviceversion/index",
                        name: "childdeviceversion",
                        component: () => import('/@/views/iot/version/childdeviceversion/index.vue'),
                        meta: {
                            title: "子设备版本",
                            isLink: "",
                            isHide: false,
                            isKeepAlive: true,
                            isAffix: false,
                            isIframe: false,
                            permissions: ["SubDeviceVersionStat.view"],
                            icon: ""
                        }
                    },
                ],
            },
            {
                path: '/modifypassword',
                name: 'ModifyPassword',
                component: () => import('/@/views/Account/modifypassword.vue'),
                meta: {
                    title: '修改密码',
                    isHide: true,
                },
            }
        ]
    }
];

// 部分前端管理的路由
export const frontEndRoutes = [
    {
        path: '/profile',
        name: 'profile',
        component: () => import('../views/Account/modifypassword.vue'),
        meta: {
            title: '个人中心',
            isHide: true,
        },
    }
];

/**
 * 定义404、401界面
 * @link 参考：https://next.router.vuejs.org/zh/guide/essentials/history-mode.html#netlify
 */
export const notFoundAndNoPower = [
    {
        path: '/:path(.*)*',
        name: 'notFound',
        component: () => import('/@/views/error/404.vue'),
        meta: {
            title: 'message.staticRoutes.notFound',
            isHide: true,
        },
    },
    {
        path: '/401',
        name: 'noPower',
        component: () => import('/@/views/error/401.vue'),
        meta: {
            title: 'message.staticRoutes.noPower',
            isHide: true,
        },
    }
];

/**
 * 定义静态路由（默认路由）
 * 此路由不要动，前端添加路由的话，请在 `dynamicRoutes 数组` 中添加
 * @description 前端控制直接改 dynamicRoutes 中的路由，后端控制不需要修改，请求接口路由数据时，会覆盖 dynamicRoutes 第一个顶级 children 的内容（全屏，不包含 layout 中的路由出口）
 * @returns 返回路由菜单数据
 */
export const staticRoutes: Array<RouteRecordRaw> = [
    {
        path: '/login',
        name: 'login',
        component: () => import('/@/views/login/index.vue'),
        meta: {
            title: '登录',
        },
    },
    {
        path: '/:path(.*)*',
        component: () => import('/@/views/error/404.vue'),
        meta: {
            title: 'message.staticRoutes.notFound',
            isHide: true,
        },
    }
];